/**
 * VqeShortcutsHelp — a shadcn <Dialog> listing the Visual Query Editor keyboard
 * shortcuts, grouped by section. Copy is read verbatim from `shortcuts.ts` (the
 * single source of truth), so this component is purely presentational: the host
 * (`VqeStatementView`) owns the controlled `open` state and the analytics
 * event that fires when the dialog is opened.
 *
 * Escape + click-outside close, ARIA role=dialog, and focus trapping are all
 * Radix Dialog defaults — we do not reimplement them. The dialog also carries
 * the visible ways out: a × in the top-right corner and a footer Close button
 * (the app's DialogContent draws no × of its own).
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md
 *   § Keyboard Shortcut Help.
 */

import { X } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { useShortcutLabel } from '../../../keybindings/hooks/useShortcutLabel';
import { shortcutKeysLabel, shortcuts } from './shortcuts';

export interface VqeShortcutsHelpProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

function sectionTestId(section: string): string {
  return `vqe-shortcuts-group-${section.toLowerCase().replace(/\s+/g, '-')}`;
}

export function VqeShortcutsHelp({ open, onOpenChange }: VqeShortcutsHelpProps) {
  // Subscribed reads so a rebinding repaints the open dialog.
  const openVqeShortcutLabel = useShortcutLabel('global.openVisualQueryEditor');
  const executeShortcutLabel = useShortcutLabel('vqe.execute');
  const keybindingLabels = {
    'global.openVisualQueryEditor': openVqeShortcutLabel,
    'vqe.execute': executeShortcutLabel,
  } as const;
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="vqe-shortcuts-help"
        className="max-h-[80vh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Close"
          data-testid="vqe-shortcuts-help-close-x"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Work the Visual Query Editor faster with these shortcuts.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {shortcuts.map((group) => (
            <section key={group.section} data-testid={sectionTestId(group.section)}>
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                {group.section}
              </h3>
              <ul className="space-y-1.5">
                {group.items.map((item) => (
                  <li
                    key={`${group.section}-${item.description}`}
                    className="flex items-start justify-between gap-4 text-sm"
                  >
                    <span className="text-muted-foreground">{item.description}</span>
                    <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                      {item.keybindingId ? keybindingLabels[item.keybindingId] : shortcutKeysLabel(item)}
                    </kbd>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={close}
            data-testid="vqe-shortcuts-help-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
